import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  BarChart3,
  TrendingUp,
  CreditCard,
  Users,
  Terminal,
  Brain,
  ShieldCheck,
  FileBarChart,
  ScrollText,
  KeyRound,
  Settings,
  Sparkles,
  Building2,
  Activity,
  IndianRupee,
  Command,
} from "lucide-react";

interface PaletteCommand {
  id:          string;
  label:       string;
  description: string;
  icon:        React.ReactNode;
  group:       "Navigate" | "Reports" | "Actions";
  path:        string;
}

const COMMANDS: PaletteCommand[] = [
  // ── Navigate ──────────────────────────────────────────────
  { id: "cockpit",  label: "Global Cockpit",    description: "Platform overview and live metrics",    icon: <BarChart3 size={15} />,    group: "Navigate", path: "/platform" },
  { id: "portfolio",label: "Portfolio Health",  description: "Fleet health scores across societies",  icon: <TrendingUp size={15} />,   group: "Navigate", path: "/platform/portfolio" },
  { id: "revenue",  label: "Revenue Analytics", description: "MRR, ARR, billing collections",         icon: <IndianRupee size={15} />,  group: "Navigate", path: "/platform/revenue" },
  { id: "tenants",  label: "Tenant Registry",   description: "All managed societies",                 icon: <Building2 size={15} />,    group: "Navigate", path: "/platform/tenants" },
  { id: "subs",     label: "Subscriptions",     description: "Plan, wallet, and billing control",     icon: <CreditCard size={15} />,   group: "Navigate", path: "/platform/subscriptions" },
  { id: "events",   label: "Event Monitor",     description: "Platform-wide event stream",            icon: <Terminal size={15} />,     group: "Navigate", path: "/platform/events" },
  { id: "anomalies",label: "Anomaly Center",    description: "AI-detected behavioural anomalies",     icon: <Brain size={15} />,        group: "Navigate", path: "/platform/anomalies" },
  { id: "security", label: "Security Intel",    description: "Login, permission and impersonation logs", icon: <ShieldCheck size={15} />, group: "Navigate", path: "/platform/security" },
  { id: "reports",  label: "Reports Hub",       description: "Operational intelligence reports",      icon: <FileBarChart size={15} />, group: "Navigate", path: "/platform/reports" },
  { id: "audit",    label: "Audit Trail",       description: "Immutable record of all platform actions", icon: <ScrollText size={15} />, group: "Navigate", path: "/platform/audit" },
  { id: "perms",    label: "Permission Center", description: "Role matrix and access management",     icon: <KeyRound size={15} />,     group: "Navigate", path: "/platform/permissions" },
  { id: "settings", label: "System Config",     description: "Infrastructure and platform settings",  icon: <Settings size={15} />,     group: "Navigate", path: "/platform/settings" },
  // ── Reports ───────────────────────────────────────────────
  { id: "r-portfolio", label: "Portfolio Report",   description: "Export fleet health → Reports Hub", icon: <TrendingUp size={15} />,  group: "Reports", path: "/platform/reports?section=portfolio" },
  { id: "r-revenue",   label: "Revenue Report",     description: "MRR and collections export",         icon: <IndianRupee size={15} />, group: "Reports", path: "/platform/reports?section=revenue" },
  { id: "r-security",  label: "Security Report",    description: "Login and anomaly summary",          icon: <ShieldCheck size={15} />, group: "Reports", path: "/platform/reports?section=security" },
  { id: "r-ocr",       label: "OCR Intelligence",   description: "Scan quality and accuracy metrics",  icon: <Activity size={15} />,   group: "Reports", path: "/platform/reports?section=ocr" },
  // ── Labs ──────────────────────────────────────────────────
  { id: "ai",       label: "AI Insights",       description: "Machine learning and NLP intelligence",  icon: <Sparkles size={15} />,    group: "Actions", path: "/platform/ai-insights" },
  { id: "registry", label: "Manage Tenants",    description: "Open tenant registry with full table",   icon: <Users size={15} />,       group: "Actions", path: "/platform/tenants" },
];

/* Fuzzy matcher — returns commands whose label or description contain every word of query */
function fuzzyMatch(cmd: PaletteCommand, q: string): boolean {
  const words = q.toLowerCase().split(/\s+/).filter(Boolean);
  const haystack = `${cmd.label} ${cmd.description}`.toLowerCase();
  return words.every(w => haystack.includes(w));
}

interface CommandPaletteProps {
  /** Called by parent to override open state externally (optional) */
  forceOpen?: boolean;
}

const CommandPalette: React.FC<CommandPaletteProps> = () => {
  const [open, setOpen]           = useState(false);
  const [query, setQuery]         = useState("");
  const [selectedIdx, setSelected] = useState(0);
  const inputRef  = useRef<HTMLInputElement>(null);
  const listRef   = useRef<HTMLDivElement>(null);
  const navigate  = useNavigate();

  /* ── Open / close keyboard shortcut ── */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setOpen(o => {
          if (!o) { setQuery(""); setSelected(0); }
          return !o;
        });
      }
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  /* ── Focus input on open ── */
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 30);
  }, [open]);

  /* ── Derived state ── */
  const filtered = query.trim()
    ? COMMANDS.filter(c => fuzzyMatch(c, query))
    : COMMANDS;

  // Build grouped list for rendering; flat list for index tracking
  const groups: Record<string, PaletteCommand[]> = {};
  filtered.forEach(cmd => {
    (groups[cmd.group] ??= []).push(cmd);
  });

  /* ── Execute selected command ── */
  const execute = useCallback((cmd: PaletteCommand) => {
    setOpen(false);
    navigate(cmd.path);
  }, [navigate]);

  /* ── Arrow / Enter navigation ── */
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelected(i => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelected(i => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      if (filtered[selectedIdx]) execute(filtered[selectedIdx]);
    }
  };

  /* ── Scroll selected item into view ── */
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-idx="${selectedIdx}"]`) as HTMLElement;
    el?.scrollIntoView({ block: "nearest" });
  }, [selectedIdx]);

  if (!open) return null;

  return (
    /* Full-screen backdrop */
    <div
      className="fixed inset-0 z-[200] flex items-start justify-center pt-[15vh] px-4"
      onClick={() => setOpen(false)}
    >
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

      {/* Palette card */}
      <div
        className="relative w-full max-w-xl bg-surface rounded-2xl shadow-2xl border border-border
                   overflow-hidden flex flex-col"
        style={{ maxHeight: "min(540px, 80vh)" }}
        onClick={e => e.stopPropagation()}
      >
        {/* ── Search input ── */}
        <div className="flex items-center gap-3 px-4 py-3.5 border-b border-border shrink-0">
          <Search size={15} className="text-muted shrink-0" />
          <input
            ref={inputRef}
            value={query}
            onChange={e => { setQuery(e.target.value); setSelected(0); }}
            onKeyDown={handleKeyDown}
            placeholder="Search commands, pages, reports…"
            className="flex-1 text-sm bg-transparent outline-none text-text placeholder:text-muted"
          />
          <div className="flex items-center gap-1 shrink-0">
            <kbd className="flex items-center gap-0.5 text-[9px] font-bold text-muted bg-slate-100
                           px-1.5 py-0.5 rounded border border-border">
              <Command size={9} />K
            </kbd>
          </div>
        </div>

        {/* ── Results ── */}
        <div ref={listRef} className="overflow-y-auto flex-1 custom-scrollbar">
          {filtered.length === 0 ? (
            <div className="py-12 text-center text-sm text-muted">
              No commands match <span className="font-semibold text-text">"{query}"</span>
            </div>
          ) : (
            Object.entries(groups).map(([group, cmds]) => (
              <div key={group}>
                <p className="sticky top-0 text-[9px] font-bold text-muted/40 uppercase tracking-[0.25em]
                              px-4 py-2 bg-slate-50/80 backdrop-blur-sm border-b border-border/50">
                  {group}
                </p>
                {cmds.map(cmd => {
                  const idx = filtered.indexOf(cmd);
                  const active = idx === selectedIdx;
                  return (
                    <button
                      key={cmd.id}
                      data-idx={idx}
                      onClick={() => execute(cmd)}
                      onMouseEnter={() => setSelected(idx)}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors
                        ${active ? "bg-primary/5" : "hover:bg-slate-50/70"}`}
                    >
                      <div className={`h-8 w-8 rounded-xl flex items-center justify-center shrink-0 transition-colors
                        ${active ? "bg-primary/10 text-primary" : "bg-slate-100 text-muted"}`}>
                        {cmd.icon}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-semibold leading-none ${active ? "text-primary" : "text-text"}`}>
                          {cmd.label}
                        </p>
                        <p className="text-[11px] text-muted mt-0.5 truncate">{cmd.description}</p>
                      </div>
                      {active && (
                        <kbd className="text-[9px] font-bold text-primary bg-primary/10 px-1.5 py-0.5
                                        rounded border border-primary/20 shrink-0">
                          ↵
                        </kbd>
                      )}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>

        {/* ── Footer ── */}
        <div className="flex items-center gap-5 px-4 py-2.5 border-t border-border bg-slate-50/60 shrink-0">
          {[
            { keys: ["↑", "↓"], label: "navigate" },
            { keys: ["↵"],      label: "open" },
            { keys: ["Esc"],    label: "close" },
          ].map(({ keys, label }) => (
            <div key={label} className="flex items-center gap-1.5 text-[10px] text-muted">
              {keys.map(k => (
                <kbd key={k} className="bg-white border border-border rounded px-1 py-0.5 text-[9px] font-semibold leading-none">
                  {k}
                </kbd>
              ))}
              <span>{label}</span>
            </div>
          ))}
          <div className="ml-auto text-[10px] text-muted/50">
            {filtered.length} result{filtered.length !== 1 ? "s" : ""}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommandPalette;
